import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { AccentButton, AuthScene, Button, Loader } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT } from "../i18n/I18nProvider";
import { confirmEmailChange, emailChangeStatus } from "../lib/auth";
import { ApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";

/**
 * E-posti muutuse kinnitusleht - kinnituskirja lingi (`/verify-email?token=…`)
 * siht (saadetud UUELE aadressile). Kinnitus on selgesõnaline nupp (POST), nii et
 * e-kirja-skännerite eelnõudmine (GET) ei jõusta muutust kogemata.
 */
export function VerifyEmailPage({
  onDone,
  authSpec,
}: {
  onDone: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();
  const glow = authSpec?.glow ?? "aurora";

  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [status, setStatus] = useState<"checking" | "valid" | "invalid" | "done">("checking");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    emailChangeStatus(token).then((s) => {
      if (s.valid) {
        setNewEmail(s.newEmail ?? "");
        setStatus("valid");
      } else {
        setStatus("invalid");
      }
    });
  }, [token]);

  async function confirm() {
    setErr(null);
    setBusy(true);
    try {
      await confirmEmailChange(token);
      setStatus("done");
    } catch (e) {
      const code = e instanceof ApiError ? String((e.body as { error?: string })?.error ?? "") : "";
      if (code === "email_taken") setErr(t("verifyEmail.taken"));
      else setStatus("invalid");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={<LanguageMenu />}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
          {status === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Loader size="md" />
              <p className="text-sm text-muted-foreground">{t("verifyEmail.checking")}</p>
            </div>
          ) : status === "invalid" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <AlertTriangle className="size-8 text-destructive" />
              <h1 className="text-xl font-bold tracking-tight">{t("verifyEmail.title")}</h1>
              <p className="text-sm text-destructive">{t("verifyEmail.invalid")}</p>
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={onDone}>
                {t("verifyEmail.continue")}
              </Button>
            </div>
          ) : status === "done" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <CheckCircle2 className="size-8 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">{t("verifyEmail.doneTitle")}</h1>
              <p className="text-sm text-muted-foreground">{t("verifyEmail.done", { email: newEmail })}</p>
              <AccentButton glow={glow} type="button" size="lg" className="w-full" onClick={onDone}>
                {t("verifyEmail.continue")}
              </AccentButton>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <Mail className="size-8 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">{t("verifyEmail.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("verifyEmail.confirm", { email: newEmail })}</p>
              {err && <p className="text-sm text-destructive">{err}</p>}
              <AccentButton glow={glow} type="button" size="lg" loading={busy} className="w-full" onClick={confirm}>
                {t("verifyEmail.confirmButton")}
              </AccentButton>
            </div>
          )}
        </div>
      </motion.div>
    </AuthScene>
  );
}
